const DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm:ss";

export const MAX_BOOKING_MONTHS_IN_ADVANCE = 15;
export const MAX_BOOKING_DAYS_IN_ADVANCE = MAX_BOOKING_MONTHS_IN_ADVANCE * 30;

export const AVAILABILITY_DATETIME_FORMAT = `${DATE_FORMAT} ${TIME_FORMAT}Z`;

const availabilityStatuses = {
  AVAILABLE: "available",
  NOT_AVAILABLE: "notAvailable",
};

const concat = <T>(a: Array<T>, b: Array<T>) => a.concat(b);
const and = (a: boolean, b: boolean) => a && b;

interface Guide {
  id: string;
}

interface Group {
  id: string;
  available: number;
  guide: Guide | null;
}

interface Attendees {
  available: number;
}

interface AvailabilitySlotResponse {
  isoDateTime: string;
  isoCanBeBookedUntil: string | null;
  status: string;
  groups: Array<Group> | null;
  attendees: Attendees;
}

export type AvailabilityResponse = Array<AvailabilitySlotResponse>;

// @todo not sure if every slot has a list of groups
interface AvailabilitySlot {
  status: string;
  groups: Array<Group>;
  attendees: Attendees;
}

type Availability = Array<AvailabilitySlot>;

function fromRaw(response: AvailabilityResponse): Availability {
  return response.map((s) => {
    return {
      status: s.status,
      groups: s.groups || [],
      attendees: s.attendees,
    };
  });
}

function isSlotAvailable(slot: AvailabilitySlot) {
  const { status } = slot;
  return status === availabilityStatuses.AVAILABLE;
}

export function createQueries(response: AvailabilityResponse) {
  const availability = fromRaw(response);

  return {
    doesGroupExist(groupId: string) {
      const match = availability.find(
        (slot) =>
          slot.status === availabilityStatuses.AVAILABLE &&
          slot.groups.length > 0 &&
          slot.groups.some((g) => g.id === groupId)
      );

      return !!match;
    },

    getGuideByGroupId(groupId: string): Guide | null {
      const match = availability.find((slot) =>
        slot.groups.some((g) => g.id === groupId)
      );

      if (!match) {
        return null;
      }

      const group = match.groups.find((g) => g.id === groupId);

      if (!group) {
        return null;
      }

      return group.guide;
    },

    doTheFirstAvailableGroupsHaveGuidesAssigned({
      first,
    }: {
      first: number;
    }): boolean {
      const availableSlots = availability.filter(isSlotAvailable);

      if (availableSlots.length === 0) {
        return false;
      }

      // some slots dont have any groups
      const groups = availableSlots
        .map((slot) => (slot.groups.length === 0 ? [{}] : slot.groups))
        .reduce(concat, []);
      const firstGroups = groups.slice(0, first);

      return firstGroups
        .map((group) => !!(group as Group).guide)
        .reduce(and, true);
    },
  };
}
